// The postcard (the save menu's second row, issue #134): the frame a PNG save would give, whole and at the same four
// output pixels a scene pixel, on a cream card with the city's name, its local time, the track that plays and
// loficities.com under it, all in the signs' font (core FONT3, with its own wider M, W, N, K and V: WIDE below). The
// card is 510×340 card pixels, 2040×1360 as a PNG: a 6×4 postcard's shape. The city and the site stand in the
// caption's first row, right under the picture, so a preview cropped to 16:9 still says both.
// A touch screen whose share sheet takes files gets the PNG there, with the city's link and line (site/js/ui/share.js);
// every other screen downloads it (site/js/app.js `postcard`). Save this frame (S) stays the bare 1920×1080 frame.
// The drawing reads no clock, page or player: drawPostcard is a function of the frame and three texts.
//   node --test tests/postcard.test.mjs
import { W, H, SCALE, FONT3, makeCanvas, rect, glyph } from '../engine/core.js';
import { download } from '../engine/recorder.js';

const EDGE = 15, CAPTION = 55;                        // paper round the picture, and under it
export const CARD = { w: W + 2 * EDGE, h: EDGE + H + CAPTION, scale: SCALE };
const LEFT = EDGE, RIGHT = CARD.w - EDGE;             // the caption's columns end where the picture does
const ROW1 = EDGE + H + 6, ROW2 = ROW1 + 29;          // the city's top; the second row's top, at its largest size
const BIG = 4, MID = 2;                               // card pixels a font pixel: the city, the other three at their largest
const GAP = 20;                                       // at least this much paper between a row's left and right text
const NOTE = ['0011', '0010', '0010', '1110', '1110'];   // a quaver ahead of the track, one font pixel's gap after it
const NOTE_W = NOTE[0].length + 2;
export const SITE = 'LOFICITIES.COM';
// paper, its edge, ink and the lighter ink of the second row: the sale plane's banner (site/js/ui/plane.js) is this cloth
const COLOURS = { paper: '#f6f0de', edge: '#c9bb98', ink: '#15161f', soft: '#5f5b6e' };

// What the track titles and the Classical pieces spell that FONT3 lacks ("soft rain, shibuya", "Für Elise (Beethoven)",
// "hazy echoes in coyoacán"): accents as FONT3 draws its own, on the top row of a three-row letter
const EXTRA = {
  ',': ['000', '000', '000', '010', '100'], '(': ['001', '010', '010', '010', '001'], ')': ['100', '010', '010', '010', '100'],
  '·': ['000', '000', '010', '000', '000'],
  'Á': ['001', '000', '010', '111', '101'], 'Ã': ['111', '000', '010', '111', '101'], 'É': ['001', '000', '111', '110', '111'],
  'Ñ': ['111', '000', '110', '101', '101'], 'Ó': ['001', '000', '111', '101', '111'], 'Ú': ['001', '000', '101', '101', '111'],
  'Ü': ['101', '000', '101', '101', '111'],
};
// The caption's own M, W, N, K and V. In FONT3's three columns M and W are an H with one more pixel, N an arch, K an
// H with one fewer and V a U with its corners off, which a billboard's two words get away with and a line like "EINE
// KLEINE NACHTMUSIK (MOZART)", "VELVET RIVER" or the site's own address doesn't. Same strokes and height, one or two
// columns wider; FONT3 itself is every city's.
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const SWAPS = { '–': '-', '—': '-', '‘': "'", '’': "'", '•': '·', '|': '·' };
/** A character's rows of '0'/'1' as the caption draws it (undefined: one it can't write). */
export const captionGlyph = ch => WIDE[ch] ?? FONT3[ch] ?? EXTRA[ch];
const glyphOf = captionGlyph;
/** How wide the card writes `s` (cardText's), in font pixels: each letter's own columns, one between letters. */
export const captionWidth = s => [...s].reduce((w, ch) => w + glyphOf(ch)[0].length + 1, 0) - (s ? 1 : 0);

/** `s` as the card can write it: upper case, an accent the font lacks as its bare letter, anything else it lacks as a space. */
export function cardText(s) {
  let out = '';
  for (let ch of String(s ?? '').normalize('NFC').toUpperCase()) {
    ch = SWAPS[ch] ?? ch;
    if (!glyphOf(ch)) ch = ch.normalize('NFD')[0];
    out += glyphOf(ch) ? ch : ' ';
  }
  return out.replace(/ +/g, ' ').trim();
}

/** `s` in at most `max` characters: cut after a whole word where one ends in the second half, with three dots for the rest. */
export function shorten(s, max) {
  if (s.length <= max) return s;
  if (max <= 3) return s.slice(0, Math.max(0, max)).trimEnd();
  const cut = s.slice(0, max - 3), space = s[max - 3] === ' ' ? cut.length : cut.lastIndexOf(' ');
  return `${(space >= cut.length / 2 ? cut.slice(0, space) : cut).replace(/[ ,·(&/:'-]+$/, '')}...`;
}

/**
 * `s` (cardText's) at the first of `sizes` (card pixels a font pixel, largest first) it is no wider than `width` card
 * pixels at, `lead` font pixels ahead of it counted in; too long for the smallest, it is shortened there: { text, k }.
 */
export function fitText(s, width, sizes, lead = 0) {
  const fits = (text, k) => (lead + captionWidth(text)) * k <= width;
  for (const k of sizes) if (fits(s, k)) return { text: s, k };
  const k = sizes.at(-1);
  let text = s;
  for (let max = s.length - 1; max >= 0 && !fits(text, k); max--) text = shorten(s, max);   // letters differ in width: try each length
  return { text, k };
}

/**
 * What the card says is playing, from the player's track info: the track's title (the radio's and Mix's too); for a
 * Classical track the piece it covers and who wrote it ("Für Elise (Beethoven)"), the name a listener knows it by;
 * for a playlist file its artist after the title. '' before the first track.
 */
export function trackLine(info) {
  if (info?.piece) return `${info.piece.name} (${info.piece.by})`;
  if (!info?.title) return '';
  return info.source === 'playlist' && info.artist ? `${info.title} · ${info.artist}` : info.title;
}

/**
 * Where everything goes, in card pixels: the picture's box and each text as { text, x, y, k, colour } (k: card pixels
 * a font pixel). The city (left) and the site (right) share the first row; under them the time (left) and the track
 * with its note (right; null with no track). The track takes the largest size that leaves the time its room: every
 * generated title and every Classical piece fits at the time's size, a longer line goes a size down, and one too long
 * for that is shortened.
 */
export function postcardLayout({ city, time, track = '' }) {
  const at = (text, k, x, y, colour) => ({ text, x, y, k, colour });
  const site = at(SITE, MID, RIGHT - captionWidth(SITE) * MID, ROW1 + 5 * (BIG - MID), COLOURS.ink);   // on the city's baseline
  const name = fitText(cardText(city), site.x - GAP - LEFT, [BIG, 3, MID]);
  const clock = fitText(`${cardText(time)} LOCAL TIME`, RIGHT - LEFT, [MID, 1]);
  const when = at(clock.text, clock.k, LEFT, ROW2, COLOURS.soft);
  const said = cardText(track), room = RIGHT - LEFT - captionWidth(when.text) * when.k - GAP;
  const song = said ? fitText(said, room, [MID, 1], NOTE_W) : null;
  const y = song && ROW2 + Math.floor(5 * (MID - song.k) / 2);                  // a smaller line sits in the middle of the row
  const x = song && RIGHT - captionWidth(song.text) * song.k;
  return {
    picture: { x: EDGE, y: EDGE, w: W, h: H },
    city: at(name.text, name.k, LEFT, ROW1 + 5 * (BIG - name.k), COLOURS.ink),
    site, time: when,
    track: song && at(song.text, song.k, x, y, COLOURS.soft),
    note: song && { x: x - NOTE_W * song.k, y, k: song.k, colour: COLOURS.soft },
  };
}

// the caption's glyphs with k card pixels a font pixel, each letter as wide as it is and one font pixel before the next
function write(c, { text, x, y, k, colour }) {
  c.save();
  c.translate(x, y);
  c.scale(k, k);
  let at = 0;
  for (const ch of text) {
    const g = glyphOf(ch);
    glyph(c, g, at, 0, colour);
    at += g[0].length + 1;
  }
  c.restore();
}

/**
 * The postcard of `frame` (a scene's W×H buffer, already rendered) as a new canvas, CARD.w × CARD.h card pixels at
 * CARD.scale: `city` its name, `time` its clock ("02:14"), `track` trackLine's (or '').
 */
export function drawPostcard({ frame, city, time, track = '' }) {
  const L = postcardLayout({ city, time, track }), p = L.picture;
  const [card, c] = makeCanvas(CARD.w, CARD.h);
  rect(c, 0, 0, CARD.w, CARD.h, COLOURS.edge);
  rect(c, 1, 1, CARD.w - 2, CARD.h - 2, COLOURS.paper);
  rect(c, p.x - 1, p.y - 1, p.w + 2, p.h + 2, COLOURS.ink);                     // the print's edge
  c.drawImage(frame, 0, 0, W, H, p.x, p.y, p.w, p.h);
  for (const t of [L.city, L.site, L.time, L.track]) if (t) write(c, t);
  if (L.note) {
    c.save();
    c.translate(L.note.x, L.note.y);
    c.scale(L.note.k, L.note.k);
    glyph(c, NOTE, 0, 0, L.note.colour);
    c.restore();
  }
  const [out, o] = makeCanvas(CARD.w * CARD.scale, CARD.h * CARD.scale, true);   // an element: pngFile reads it with toDataURL
  o.drawImage(card, 0, 0, out.width, out.height);
  return out;
}

/** The file's name, after the frame's ("lofi-cities-tokyo.png"): a look other than the city's first says so. */
export const postcardName = look => `lofi-cities-${look}-postcard.png`;

/**
 * `canvas` as a PNG file, made at once: a share sheet only opens while the tap that asked for it still counts, and
 * canvas.toBlob answers later.
 */
export function pngFile(canvas, name) {
  const bin = atob(canvas.toDataURL('image/png').split(',')[1]), bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type: 'image/png' });
}

const takes = (nav, data) => { try { return typeof nav?.share === 'function' && nav.canShare?.(data) === true; } catch { return false; } };
/** Whether a postcard goes to the share sheet here: a touch screen whose browser says it shares PNG files. */
export const sharesPostcards = ({ touch, nav = globalThis.navigator }) =>
  Boolean(touch) && takes(nav, { files: [new File([], postcardName('city'), { type: 'image/png' })] });

/**
 * Hands the card over. On a touch screen whose share sheet takes `file` with `share` (shareOf's { url, title, text }),
 * the sheet; everywhere else, and where the sheet won't open, `save` (a download). Call it in the tap itself.
 * Resolves to { shared } once the card is shared or saved, to null when the sheet was closed without sharing.
 */
export async function sendPostcard({ file, share = {}, touch, nav = globalThis.navigator, save = f => download(f, f.name) }) {
  const data = { files: [file], ...share };
  if (touch && takes(nav, data)) {
    try {
      await nav.share(data);
      return { shared: true };
    } catch (e) {
      if (e?.name === 'AbortError') return null;      // closed without sharing
      console.error(e);                               // the sheet didn't open: the file downloads
    }
  }
  save(file);
  return { shared: false };
}
